import { Injectable } from '@angular/core';
import { NextAvailableSlotInfo, normalizeSM, SlotDayGroup, SlotOption } from './fulle-saleMethod.interface';
import { PointOfSaleClosing, PointOfSaleDayTimetable, PointOfSaleSummary, PointOfSaleTimetable } from '../fulle-pointOfSale/fulle-pointOfSale.service.interface';
import { FulleBaseclientService } from '../../../apiclient/fulle-baseclient.service';
import { SaleMethodSlim } from 'src/app/components/order/fulle-selection-SM/fulle-selection-SM.interface';
import { PanierService } from 'src/app/services/manage/panier/panier.service';
import { ToolsService } from 'src/app/services/tools/tools.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class FulleSaleMethodService {
    public static TAG_STORAGE: string = "sale_method"
    private currSaleMeth: BehaviorSubject<any> = new BehaviorSubject(null);

    constructor(
        private fulleBaseclient: FulleBaseclientService,
        private panierService: PanierService,
        private tools: ToolsService,
    ) { }

    // Get sales methods by point of sale id SLIM
    async getSaleMethods(
        merchantId: string,
        fieldsList?: string,
        includeList?: string
    ): Promise<SaleMethodSlim[]> {
        return this.fulleBaseclient
            .getBaseSlim(
                'sale_methods',
                { merchantId, fields: fieldsList, include: includeList }
            )
            .then(resp => resp.data);
    }

    // calcule le prochain slot available pour une commande.
    getNextAvailableSlot(sm: SaleMethodSlim, pos: PointOfSaleSummary, now: Date): NextAvailableSlotInfo {
        if (!sm || sm.enableDateHour !== true) return { kind: 'NONE' };
        if (!pos || !pos.timetables?.length) return { kind: 'NONE' };

        // const gap = pos.limit_order_gap ?? 0; ancienne version que je ne comprends pas, pos n'a pas de limit_order_gap, seul sm en a...
        const gap = 0;
        const readyAt = new Date(now.getTime() + gap * 60000);

        for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
            const date = this.addDays(readyAt, dayOffset);
            const dow = date.getDay();

            const dayEntry = pos.timetables.find((d: PointOfSaleDayTimetable) => d.day === dow);
            if (!dayEntry) continue;

            const slots = this.collectSlotsForSm(dayEntry.timetables, sm.id);
            if (!slots.length) continue;

            for (const slot of slots) {
                const startDT = this.combineDateAndTime(date, slot.start);
                const endDT = this.combineDateAndTime(date, slot.end);

                const effectiveStart = readyAt >= startDT ? readyAt : startDT;
                if (effectiveStart >= endDT) continue;

                // Arrondir au prochain quart d'heure
                const candidateTime = this.roundToNextQuarterHour(effectiveStart);

                if (candidateTime >= endDT) continue;
                if (this.isInClosing(candidateTime, pos.closings)) continue;

                if (candidateTime.getTime() === this.roundToNextQuarterHour(readyAt).getTime()) {
                    return { kind: 'NOW', at: candidateTime };
                }

                if (candidateTime > readyAt) {
                    return { kind: 'NEXT', at: candidateTime };
                }
            }
        }

        return { kind: 'NONE' };
    }


    // Calcule les slots possibles pour les afficher lorsque le client clique sur selectSM()
    getAvailableSlots(sm: SaleMethodSlim, pos: PointOfSaleSummary, now: Date, days = 7): SlotDayGroup[] {
        if (!sm || sm.enableDateHour !== true) return [];
        if (!pos || !pos.timetables?.length) return [];

        const gap = 0;
        const readyAt = new Date(now.getTime() + gap * 60000);

        const groups: SlotDayGroup[] = [];

        for (let dayOffset = 0; dayOffset < days; dayOffset++) {
            const baseDate = this.startOfDay(this.addDays(readyAt, dayOffset));
            const dow = baseDate.getDay();

            const dayEntry = pos.timetables.find((d: PointOfSaleDayTimetable) => d.day === dow);
            if (!dayEntry) continue;

            const slots = this.collectSlotsForSm(dayEntry.timetables, sm.id);
            if (!slots.length) continue;

            const daySlots: SlotOption[] = [];

            for (const slot of slots) {
                const startDT = this.combineDateAndTime(baseDate, slot.start);
                const endDT = this.combineDateAndTime(baseDate, slot.end);

                if (readyAt >= endDT) continue;

                const effectiveStart = readyAt >= startDT ? readyAt : startDT;
                if (effectiveStart >= endDT) continue;

                // Générer des créneaux de 15 minutes
                const quarterHourSlots = this.generateQuarterHourSlots(
                    effectiveStart,
                    endDT,
                    readyAt,
                    pos.closings
                );

                daySlots.push(...quarterHourSlots);
            }

            if (daySlots.length) {
                daySlots.sort((a, b) => a.start.getTime() - b.start.getTime());
                groups.push({ date: baseDate, dow, slots: daySlots });
            }
        }

        return groups;
    }

    // Nouvelle méthode helper pour générer les créneaux de 15 minutes
    private generateQuarterHourSlots(
        startTime: Date,
        endTime: Date,
        readyAt: Date,
        closings: PointOfSaleClosing[] | undefined
    ): SlotOption[] {
        const slots: SlotOption[] = [];
        const QUARTER_HOUR_MS = 15 * 60 * 1000; // 15 minutes en millisecondes

        // Arrondir le début au prochain quart d'heure si nécessaire
        let currentStart = this.roundToNextQuarterHour(startTime);

        while (currentStart < endTime) {
            const currentEnd = new Date(currentStart.getTime() + QUARTER_HOUR_MS);

            // Vérifier que le créneau ne dépasse pas l'heure de fin
            if (currentEnd > endTime) break;

            // Vérifier que le créneau n'est pas dans une période de fermeture
            if (!this.isInClosing(currentStart, closings)) {
                // Vérifier que le créneau est après l'heure de préparation minimale
                if (currentStart >= readyAt) {
                    slots.push({
                        start: new Date(currentStart),
                        end: new Date(currentEnd)
                    });
                }
            }

            currentStart = new Date(currentStart.getTime() + QUARTER_HOUR_MS);
        }

        return slots;
    }

    // Helper pour arrondir au prochain quart d'heure
    private roundToNextQuarterHour(date: Date): Date {
        const result = new Date(date);
        const minutes = result.getMinutes();
        const seconds = result.getSeconds();
        const milliseconds = result.getMilliseconds();

        // Si on est exactement sur un quart d'heure, on garde cette heure
        if (minutes % 15 === 0 && seconds === 0 && milliseconds === 0) {
            return result;
        }

        // Sinon, arrondir au prochain quart d'heure
        const nextQuarterMinutes = Math.ceil(minutes / 15) * 15;

        if (nextQuarterMinutes === 60) {
            result.setHours(result.getHours() + 1);
            result.setMinutes(0);
        } else {
            result.setMinutes(nextQuarterMinutes);
        }

        result.setSeconds(0);
        result.setMilliseconds(0);

        return result;
    }

    // ---------- Helpers ----------

    private collectSlotsForSm(timetables: PointOfSaleTimetable[], smId: number) {
        const list = timetables ?? [];

        // On commence par charger la timetable qui correspond a la smId
        const specific = list
            .filter(t => t?.id_sale_method === smId)
            .flatMap(t => t.timeslots ?? []);

        // Puis si elle existe on la laisse mais si elle n'existe pas ou prendre la id_sale_mehtod === 0 (les horaires par defaut)
        const picked = specific.length
            ? specific
            : list
                .filter(t => t?.id_sale_method === 0)
                .flatMap(t => t.timeslots ?? []);

        return picked.sort((a, b) => a.start.localeCompare(b.start));
    }

    private combineDateAndTime(date: Date, time: string): Date {
        const [h, m, s] = time.split(':').map(Number);
        const d = new Date(date);
        d.setHours(h, m, s || 0, 0);
        return d;
    }

    private addDays(date: Date, days: number): Date {
        const d = new Date(date);
        d.setDate(d.getDate() + days);
        return d;
    }

    private isInClosing(date: Date, closings: PointOfSaleClosing[] = []): boolean {
        if (!closings?.length) return false;

        for (const c of closings) {
            const start = new Date(c.date_from + 'T00:00:00');
            const end = new Date(c.date_to + 'T23:59:59');

            if (date < start || date > end) continue;

            // fermeture journée entière
            if (!c.hour_from && !c.hour_to) return true;

            const [h1, m1] = c.hour_from.split(':').map(Number);
            const [h2, m2] = c.hour_to.split(':').map(Number);

            const minutes = date.getHours() * 60 + date.getMinutes();
            const fromMin = h1 * 60 + m1;
            const toMin = h2 * 60 + m2;

            if (minutes >= fromMin && minutes <= toMin) return true;
        }

        return false;
    }

    private startOfDay(d: Date): Date {
        const x = new Date(d);
        x.setHours(0, 0, 0, 0);
        return x;
    }

    // Set the sale method into the localstorage and observable
    setSaleMeth(sm: SaleMethodSlim, same: boolean = false): void {
        if (!same) {
            this.panierService.removePanier();
            this.tools.removeStor("info_notes");
        }
        if (sm) {
            sm = normalizeSM(sm);
            this.parseLimitOrderGap(sm);
            this.parseDisableAuth(sm);
        }
        this.tools.setStor(FulleSaleMethodService.TAG_STORAGE, sm);
        this.currSaleMeth.next(sm);
    }

    // Nettoie la sale method courante (storage + observable) et optionnellement le panier / notes
    removeSaleMeth(options?: { keepCart?: boolean; keepInfoNotes?: boolean }): void {
        const keepCart = options?.keepCart ?? false;
        const keepInfoNotes = options?.keepInfoNotes ?? false;

        if (!keepCart) {
            this.panierService.removePanier();
        }

        if (!keepInfoNotes) {
            this.tools.removeStor("info_notes");
        }

        this.tools.removeStor(FulleSaleMethodService.TAG_STORAGE);

        // Important: prévenir toute l’app que la SM a disparu
        this.currSaleMeth.next(null);
    }

    parseLimitOrderGap(sm: SaleMethodSlim) {
        sm = normalizeSM(sm);
        // console.log("FulleSaleMethodService - parseLimitOrderGap - sm : ", sm)
        const gap = sm.limitOrderGap % 60 === 0 ? sm.limitOrderGap / 60 : sm.limitOrderGap;
        // console.log("FulleSaleMethodService - parseLimitOrderGap - gap : ", gap)
        let timeUnit = sm.limitOrderGap % 60 === 0 ? "heure" : "minute";
        // console.log("FulleSaleMethodService - parseLimitOrderGap - timeUnit : ", timeUnit)
        gap > 0 ? timeUnit += "s" : "";
        if (gap)
            sm.limitOrderGapStr = gap + " " + timeUnit;
    }

    parseDisableAuth(sm: SaleMethodSlim) {
        sm.disableAuthentificationInfoNotes = sm.disableAuthentificationInfoNotes ? sm.disableAuthentificationInfoNotes : null;
    }
}
